/* The second-factor step's routes (app/second_factor_routes.py, AR-290).
 *
 * Server state through TanStack Query, as every read is (CLAUDE.md section 4).
 * The status query is what AppShell gates the product on.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../auth";
import { fetchJson, postJson } from "./envelope";
import { refusalDetail } from "./refusal";

export type SecondFactorMethod = "totp" | "email" | "recovery";

export interface StepStatus {
  required: boolean;
  passed: boolean;
  methods: Exclude<SecondFactorMethod, "recovery">[];
  recovery_codes_left: number;
}

export interface TotpEnrolment {
  otpauth_uri: string;
  secret: string;
  qr_svg: string;
}

export interface VerifyResult {
  passed: boolean;
  recovery_codes: string[] | null;
}

const STATUS_KEY = ["second-factor", "status"] as const;

export function useStepStatus(enabled: boolean) {
  return useQuery({
    queryKey: STATUS_KEY,
    queryFn: () =>
      fetchJson<StepStatus>(
        "/api/second-factor/status",
        (status) => `the sign-in step could not be checked (${status})`,
      ),
    enabled,
    retry: false,
  });
}

export function useStartTotp() {
  return useMutation({
    mutationFn: () =>
      postJson<TotpEnrolment>(
        "/api/second-factor/totp",
        {},
        (status) => `the authenticator could not be set up (${status})`,
      ),
  });
}

export function useSendEmailCode() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiFetch("/api/second-factor/email-code", { method: "POST" });
      if (!response.ok) {
        throw new Error(await refusalDetail(response, `no code was sent (${response.status})`));
      }
    },
  });
}

/* Not refreshed on success: recovery codes are shown once, and refreshing the
 * status would replace the step with the product before they were read. The
 * screen calls `useFinishStep` when the person moves on. */
export function useVerify() {
  return useMutation({
    mutationFn: (body: { method: SecondFactorMethod; code: string }) =>
      postJson<VerifyResult>(
        "/api/second-factor/verify",
        body,
        (status) => `the code was refused (${status})`,
      ),
  });
}

export function useFinishStep() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: STATUS_KEY });
}
